import { useState, type FC } from "react";
import Tooltip from "../Tooltip";
import Body from "../../atoms/typography/Body";
import { theme } from "../../../theme";
import IconButton from "../../atoms/buttons/IconButton";
import Modal from "../Modal";
import { HorizontalSpacer } from "../../atoms/Spacers";
import ActionButton from "../../atoms/buttons/ActionButton";
import Alert from "../Alert";
import DefaultButton from "../../atoms/buttons/DefaultButton";
import { CrossIcon } from "../../atoms/icons/CrossIcon";
import MultilineTextField from "../../atoms/inputs/MultilineTextField";
import { useRejectInvoiceUploadUnit } from "../../../graphql/hooks/documentHandler/useRejectInvoiceUploadUnit";
import { useRejectInvoiceUploadDocument } from "../../../graphql/hooks/documentHandler/useRejectInvoiceUploadDocument";
import { useTranslation } from "react-i18next";
import Divider from "../../atoms/Divider";
import Checkbox from "../../atoms/inputs/Checkbox";
import TextField from "../../atoms/inputs/TextField";
import { useRejectionEmailTemplates } from "../../../graphql/hooks/contentful/useRejectionEmailTemplates";
import Select from "../../atoms/inputs/Select";
import SmallLoadingSpinner from "../../atoms/SmallLoadingSpinner";
import BodySmall from "../../atoms/typography/BodySmall";
import { SlideOutHeight } from "../../atoms/SlideOutHeight";
import { useSendDocumentRejectionEmail } from "../../../graphql/hooks/documentHandler/useSendDocumentRejectionEmail";
import { validateEmail } from "../../../utils/validation";

type Props =
  | {
      isDocument: true;
      documentId: string;
      uploadUnitId?: never;
      onClearSelection: () => void;
      fileId?: string;
      emailSuggestion?: string;
    }
  | {
      isDocument: false;
      uploadUnitId: string;
      documentId?: never;
      onClearSelection: () => void;
      fileId?: never;
      emailSuggestion?: string;
    };

const RejectDocumentButton: FC<Props> = ({
  documentId,
  isDocument,
  uploadUnitId,
  onClearSelection,
  fileId,
  emailSuggestion,
}) => {
  const { t } = useTranslation();
  const [modalOpen, setModalOpen] = useState(false);
  const [internalMessage, setInternalMessage] = useState("");
  const [sendClientEmail, setSendClientEmail] = useState(true);
  const [selectedTemplateId, setSelectedTemplateId] = useState<
    string | undefined
  >(undefined);
  const [clientMessage, setClientMessage] = useState("");
  const [clientRecipientEmail, setClientRecipientEmail] = useState(
    emailSuggestion || "",
  );
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const [attachFile, setAttachFile] = useState(false);

  const {
    rejectionTemplates,
    error: rejectionEmailTemplatesError,
    loading: rejectionEmailTemplatesLoading,
  } = useRejectionEmailTemplates();

  const onCompletedRejection = (result?: any) => {
    if (result?.documentHandler?.rejectInvoiceUploadUnit?.success === true) {
      setSuccessMessage(
        isDocument
          ? t("documentHandler.documentRejected")
          : t("documentHandler.uploadUnitRejected"),
      );
      onClearSelection();
    } else if (
      result?.documentHandler?.rejectInvoiceUploadDocument?.success === true
    ) {
      setSuccessMessage(
        isDocument
          ? t("documentHandler.documentRejected")
          : t("documentHandler.uploadUnitRejected"),
      );
      onClearSelection();
    } else {
      setErrorMessage(t("documentHandler.rejectFailed"));
    }
  };
  const onErrorRejection = () => {
    setErrorMessage(t("documentHandler.rejectFailed"));
  };
  const { rejectUploadUnit, loading, error } = useRejectInvoiceUploadUnit({
    onCompleted: onCompletedRejection,
    onError: onErrorRejection,
  });
  const {
    rejectUploadDocument,
    loading: documentRejectLoading,
    error: documentRejectError,
  } = useRejectInvoiceUploadDocument({
    onCompleted: onCompletedRejection,
    onError: onErrorRejection,
  });

  const onClickReject = async () => {
    if (isDocument) {
      try {
        await rejectUploadDocument({
          variables: {
            input: {
              uploadDocumentId: documentId,
              comment: internalMessage,
            },
          },
        });
      } catch (e) {
        console.error("Error rejecting upload document:", e);
        setErrorMessage(t("documentHandler.rejectFailed"));
        return;
      }
    } else {
      try {
        await rejectUploadUnit({
          variables: {
            input: {
              comment: internalMessage,
              uploadUnitId: uploadUnitId,
            },
          },
        });
      } catch (e) {
        console.error("Error rejecting upload unit:", e);
        setErrorMessage(t("documentHandler.rejectFailed"));
        return;
      }
    }
  };

  const onClickRejectButton = (e: React.MouseEvent) => {
    e.stopPropagation();
    setModalOpen(true);
  };

  const getRejectionEmailOptions = (): Array<{
    value: string;
    label: string;
  }> => {
    if (!rejectionTemplates || !rejectionTemplates.items) return [];

    const templateOptions = rejectionTemplates.items.map((item) => ({
      value: item.id,
      label: item.title,
    }));

    return [{ value: "", label: "Välj mall" }, ...templateOptions];
  };

  const onSelectRejectionTemplate = (rejectionTemplateId: string) => {
    setSelectedTemplateId(rejectionTemplateId);
    setClientMessage(
      rejectionTemplates?.items?.find((item) => item.id === rejectionTemplateId)
        ?.content || "",
    );
  };

  const onCompletedSendRejectionEmailToClient = (data: any) => {
    if (data?.documentHandler?.sendDocumentRejectionEmail?.success === true) {
      console.log("Rejection email sent to client successfully.");
      setSelectedTemplateId(undefined);
      setClientMessage("");
      setClientRecipientEmail("");
      onClickReject();
    } else {
      setErrorMessage(t("documentHandler.sendRejectionEmailFailed"));
    }
  };
  const onErrorSendRejectionEmailToClient = (error: any) => {
    console.error("Error sending rejection email to client:", error);
    setErrorMessage(t("documentHandler.sendRejectionEmailFailed"));
  };

  const {
    sendDocumentRejectionEmail,
    error: sendDocumentRejectionError,
    loading: sendDocumentRejectLoading,
  } = useSendDocumentRejectionEmail({
    onCompleted: onCompletedSendRejectionEmailToClient,
    onError: onErrorSendRejectionEmailToClient,
  });

  const onSendRejectionEmailToClient = async () => {
    if (
      !clientRecipientEmail ||
      !clientMessage ||
      validateEmail(clientRecipientEmail) === false
    ) {
      console.log(clientRecipientEmail, clientMessage);
      setErrorMessage(t("documentHandler.invalidClientEmailOrMessage"));
      return;
    }

    try {
      await sendDocumentRejectionEmail({
        variables: {
          input: {
            emailRecipient: clientRecipientEmail,
            rejectionReason: clientMessage,
            fileId: attachFile ? fileId : undefined,
          },
        },
      });
    } catch (e) {
      console.error("Error sending rejection email to client:", e);
      setErrorMessage(e?.toString() ?? t("errors.somethingWentWrong"));
    }
  };

  if (sendDocumentRejectionError) {
    setErrorMessage(t(sendDocumentRejectionError?.toString()));
  }

  return (
    <div>
      <Tooltip
        content={
          <Body color={theme.colors.white}>
            {t("documentHandler.reject")}{" "}
            {isDocument
              ? t("documentHandler.document").toLowerCase()
              : t("documentHandler.uploadUnit").toLowerCase()}
          </Body>
        }
      >
        <IconButton
          icon={<CrossIcon color={theme.colors.error} />}
          color={theme.colors.error20}
          onClick={onClickRejectButton}
          size="medium"
        />
      </Tooltip>

      {modalOpen && (
        <Modal
          onClose={() => setModalOpen(false)}
          closeIcon
          width={600}
          title={`${t("documentHandler.reject")} 
              ${
                isDocument
                  ? t("documentHandler.document").toLowerCase()
                  : t("documentHandler.uploadUnit").toLowerCase()
              }`}
        >
          <Body>
            {isDocument
              ? t("documentHandler.confirmRejectDocument")
              : t("documentHandler.confirmRejectUploadUnit")}
          </Body>

          <HorizontalSpacer spacing={2} />

          <MultilineTextField
            label={t("common.comment")}
            placeholder={t("documentHandler.provideInternalReasonForRejection")}
            value={internalMessage}
            onChange={(value) => setInternalMessage(value)}
          />

          {(errorMessage ||
            error ||
            documentRejectError ||
            sendDocumentRejectionError) && (
            <Alert
              type="error"
              title={errorMessage}
              message={errorMessage || error?.toString()}
            />
          )}

          {successMessage && <Alert title={successMessage} type="success" />}

          <HorizontalSpacer spacing={1} />
          <Divider />
          <HorizontalSpacer spacing={1} />

          <Checkbox
            title={t("documentHandler.sendAnEmailToTheClient")}
            checked={sendClientEmail}
            onChange={() => setSendClientEmail(!sendClientEmail)}
          />
          <HorizontalSpacer spacing={1} />

          {isDocument && (
            <Checkbox
              title={t("documentHandler.sendDocumentAsAttachment")}
              checked={attachFile}
              onChange={() => setAttachFile(!attachFile)}
              disabled={!fileId}
            />
          )}

          <SlideOutHeight isVisible={sendClientEmail}>
            <div>
              <HorizontalSpacer spacing={1} />
              <div style={{ display: "flex", gap: 24 }}>
                <TextField
                  label={t("random.recipientEmail")}
                  value={clientRecipientEmail}
                  onChange={(v) => setClientRecipientEmail(v)}
                />
                {rejectionEmailTemplatesLoading ? (
                  <SmallLoadingSpinner />
                ) : (
                  <Select
                    options={getRejectionEmailOptions()}
                    value={selectedTemplateId}
                    onChange={onSelectRejectionTemplate}
                    label={t("documentHandler.selectRejectionEmailTemplate")}
                  />
                )}
                {rejectionEmailTemplatesError && (
                  <BodySmall color={theme.colors.error}>
                    {t("errors.couldNotLoadTemplates")}
                  </BodySmall>
                )}
              </div>
              <HorizontalSpacer spacing={0.5} />
              <MultilineTextField
                label={t("random.message")}
                placeholder={t(
                  "documentHandler.provideRejectionMessageToClient",
                )}
                value={clientMessage}
                onChange={(value) => setClientMessage(value)}
              />
              <HorizontalSpacer spacing={1} />
            </div>
          </SlideOutHeight>

          <div style={{ display: "flex", gap: 32, justifyContent: "center" }}>
            <DefaultButton
              title={t("common.cancel")}
              onClick={() => setModalOpen(false)}
            />
            <ActionButton
              title={t("documentHandler.reject")}
              loading={
                loading || documentRejectLoading || sendDocumentRejectLoading
              }
              onClick={
                sendClientEmail ? onSendRejectionEmailToClient : onClickReject
              }
              color={theme.colors.error}
              icon={<CrossIcon color={theme.colors.white} />}
              disabled={!internalMessage}
            />
          </div>
        </Modal>
      )}
    </div>
  );
};

export default RejectDocumentButton;
